/**
 * Analytics Provider
 *
 * React context provider for analytics service.
 * Provides analytics service to the component tree.
 */

import { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import {
  AnalyticsService,
  AnalyticsConfig,
  AnalyticsProvider as IAnalyticsProvider,
  createAnalyticsService,
} from '../services/analytics.service';

// ═══════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════

interface AnalyticsContextValue {
  /** Analytics service instance */
  analytics: AnalyticsService;
  /** Whether analytics is enabled */
  isEnabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER PROPS
// ═══════════════════════════════════════════════════════════════════════

export interface AnalyticsProviderProps {
  children: ReactNode;
  /**
   * Analytics service configuration
   */
  config?: Partial<AnalyticsConfig>;
  /**
   * Pre-configured analytics service instance
   * If provided, config will be ignored
   */
  instance?: AnalyticsService;
  /**
   * Additional analytics providers to register
   */
  providers?: IAnalyticsProvider[];
  /**
   * Called when analytics service is initialized
   */
  onInit?: (analytics: AnalyticsService) => void;
}

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════

/**
 * Analytics Provider Component
 *
 * Wraps the application with analytics context.
 *
 * @example
 * ```tsx
 * // Basic usage with config
 * <AnalyticsProvider config={{ appName: 'my-app', debug: true }}>
 *   <App />
 * </AnalyticsProvider>
 *
 * // With custom providers
 * <AnalyticsProvider
 *   config={{ appName: 'my-app' }}
 *   providers={[mixpanelProvider, amplitudeProvider]}
 * >
 *   <App />
 * </AnalyticsProvider>
 *
 * // With pre-configured instance
 * const analytics = createAnalyticsService({ appName: 'my-app' });
 * await analytics.registerProvider(customProvider);
 *
 * <AnalyticsProvider instance={analytics}>
 *   <App />
 * </AnalyticsProvider>
 * ```
 */
export function AnalyticsProvider({
  children,
  config,
  instance,
  providers,
  onInit,
}: AnalyticsProviderProps): ReactNode {
  // Create or use provided analytics service
  const analytics = useMemo(() => {
    return instance || createAnalyticsService(config);
  }, [instance, config]);

  // Register additional providers
  useEffect(() => {
    if (providers && providers.length > 0) {
      const registerProviders = async () => {
        for (const provider of providers) {
          await analytics.registerProvider(provider);
        }
      };
      registerProviders();
    }
  }, [analytics, providers]);

  // Call onInit callback
  useEffect(() => {
    onInit?.(analytics);
  }, [analytics, onInit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only destroy if we created the instance
      if (!instance) {
        analytics.destroy();
      }
    };
  }, [analytics, instance]);

  const contextValue: AnalyticsContextValue = useMemo(
    () => ({
      analytics,
      isEnabled: analytics.isEnabled(),
    }),
    [analytics]
  );

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Access the analytics context
 * @throws Error if used outside of AnalyticsProvider
 */
export function useAnalyticsContext(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}

/**
 * Access the analytics service
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const analytics = useAnalytics();
 *
 *   const handleClick = () => {
 *     analytics.trackEvent('button_clicked', { buttonName: 'submit' });
 *   };
 *
 *   return <button onClick={handleClick}>Submit</button>;
 * }
 * ```
 */
export function useAnalytics(): AnalyticsService {
  const { analytics } = useAnalyticsContext();
  return analytics;
}

/**
 * Check if analytics is enabled
 */
export function useAnalyticsEnabled(): boolean {
  const { isEnabled } = useAnalyticsContext();
  return isEnabled;
}
